import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, Snackbar, Alert,
  CircularProgress, Switch
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  adminListUsers, adminCreateUser, adminUpdateRole, adminUpdateUserClient, adminToggleUser,
  adminDeleteUser, adminGetAccess, adminAddAccess, adminUpdateAccess,
  adminDeleteAccess, adminListClients, adminCreateClient, adminUpdateClient,
  adminDeleteClient, adminListRoles, adminAddTmId, adminDeleteTmId
} from '../services/api';
import './AdminPortal.css';

/** Super-admin lists: users and clients. Writes sit in one Edit dialog per row. */
const AdminPortal = ({ instance }) => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);

  const [activeTab, setActiveTab] = useState(0);
  const [clientFilter, setClientFilter] = useState(null);

  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [accessEntries, setAccessEntries] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', client_id: '', roleid: '' });
  const [newAccess, setNewAccess] = useState({ tm_id: '', access_type: 'both' });

  const [selectedClient, setSelectedClient] = useState(null);
  const [newTmId, setNewTmId] = useState('');
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ clientname: '', logo: '' });
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [editClient, setEditClient] = useState({ id: null, clientname: '', logo: '' });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  /** Only shows full spinner on first load; subsequent calls silently refresh */
  const loadData = useCallback(async () => {
    try {
      const [u, c, r] = await Promise.all([
        adminListUsers(instance),
        adminListClients(instance),
        adminListRoles(instance)
      ]);
      setUsers(u);
      setClients(c);
      setRoles(r);
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to load data', 'error');
    } finally {
      setInitialLoad(false);
    }
  }, [instance]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredUsers = useMemo(() => {
    if (!clientFilter) return users;
    return users.filter(u => u.clientname === clientFilter);
  }, [users, clientFilter]);

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.client_id || !newUser.roleid) {
      notify('All fields are required', 'warning'); return;
    }
    try {
      await adminCreateUser(instance, newUser);
      notify('User added');
      setAddUserOpen(false);
      setNewUser({ email: '', client_id: '', roleid: '' });
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to add user', 'error');
    }
  };

  /**
   * Moving a user to another client deletes their access on the server.
   * The open dialog must follow that client, or the next add posts the old id.
   */
  const handleClientChange = async (email, clientId) => {
    const clientname = clients.find(c => c.id === clientId)?.clientname || '';
    const next = { client_id: clientId, clientname, access_count: 0 };
    setUsers(prev => prev.map(u => u.email === email ? { ...u, ...next } : u));
    setSelectedUser(prev => (prev && prev.email === email ? { ...prev, ...next } : prev));
    setAccessEntries([]);
    try {
      await adminUpdateUserClient(instance, email, clientId);
      notify('Client updated (access cleared — reassign tm_ids)');
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to update client', 'error');
      const [u, entries] = await Promise.all([
        adminListUsers(instance),
        adminGetAccess(instance, email)
      ]);
      setUsers(u);
      const fresh = u.find(user => user.email === email);
      if (fresh) setSelectedUser(prev => (prev && prev.email === email ? fresh : prev));
      setAccessEntries(entries);
    }
  };

  const handleRoleChange = async (email, roleid) => {
    const rolename = roles.find(r => r.id === roleid)?.rolename;
    const next = rolename ? { roleid, rolename } : { roleid };
    setUsers(prev => prev.map(u => u.email === email ? { ...u, ...next } : u));
    setSelectedUser(prev => (prev && prev.email === email ? { ...prev, ...next } : prev));
    try {
      await adminUpdateRole(instance, email, roleid);
      notify('Role updated');
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to update role', 'error');
      const u = await adminListUsers(instance);
      setUsers(u);
      const fresh = u.find(user => user.email === email);
      if (fresh) setSelectedUser(prev => (prev && prev.email === email ? fresh : prev));
    }
  };

  const handleToggleUser = async (email) => {
    setUsers(prev => prev.map(u => u.email === email ? { ...u, is_allowed: !u.is_allowed } : u));
    try {
      await adminToggleUser(instance, email);
      notify('User access toggled');
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to toggle user', 'error');
      loadData();
    }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete ${email} and all their access entries?`)) return;
    try {
      await adminDeleteUser(instance, email);
      setUsers(prev => prev.filter(u => u.email !== email));
      if (selectedUser?.email === email) {
        setAccessDialogOpen(false);
        setSelectedUser(null);
        setAccessEntries([]);
      }
      notify('User deleted');
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to delete user', 'error');
    }
  };

  const openAccessDialog = async (user) => {
    setSelectedUser(user);
    setAccessDialogOpen(true);
    setAccessLoading(true);
    try {
      const entries = await adminGetAccess(instance, user.email);
      setAccessEntries(entries);
    } catch (err) {
      notify('Failed to load access entries', 'error');
      setAccessEntries([]);
    } finally {
      setAccessLoading(false);
    }
  };

  const closeAccessDialog = () => {
    setAccessDialogOpen(false);
    setNewAccess({ tm_id: '', access_type: 'both' });
  };

  const handleAddAccess = async () => {
    if (!newAccess.tm_id) { notify('Select a tm_id', 'warning'); return; }
    try {
      await adminAddAccess(instance, selectedUser.email, {
        client_id: selectedUser.client_id,
        tm_id: parseInt(newAccess.tm_id),
        access_type: newAccess.access_type
      });
      notify('Access added');
      setNewAccess({ tm_id: '', access_type: 'both' });
      const entries = await adminGetAccess(instance, selectedUser.email);
      setAccessEntries(entries);
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to add access', 'error');
    }
  };

  const handleUpdateAccess = async (accessId, newType) => {
    setAccessEntries(prev =>
      prev.map(e => e.id === accessId ? { ...e, access_type: newType } : e)
    );
    try {
      await adminUpdateAccess(instance, accessId, newType);
      notify('Access type updated');
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to update', 'error');
      const entries = await adminGetAccess(instance, selectedUser.email);
      setAccessEntries(entries);
    }
  };

  const handleDeleteAccess = async (accessId) => {
    setAccessEntries(prev => prev.filter(e => e.id !== accessId));
    try {
      await adminDeleteAccess(instance, accessId);
      notify('Access entry removed');
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  const refreshClients = async () => {
    const c = await adminListClients(instance);
    setClients(c);
    if (selectedClient) {
      setSelectedClient(c.find(cl => cl.id === selectedClient.id) || null);
    }
  };

  const handleAddClient = async () => {
    if (!newClient.clientname.trim()) { notify('Client name is required', 'warning'); return; }
    try {
      await adminCreateClient(instance, {
        clientname: newClient.clientname.trim(),
        logo: newClient.logo.trim() || null
      });
      notify('Client added');
      setNewClient({ clientname: '', logo: '' });
      setAddClientOpen(false);
      await refreshClients();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to add client', 'error');
    }
  };

  const openEditClient = (client) => {
    setSelectedClient(client);
    setEditClient({ id: client.id, clientname: client.clientname, logo: client.logo || '' });
    setNewTmId('');
    setEditClientOpen(true);
  };

  const handleUpdateClient = async () => {
    if (!editClient.clientname.trim()) { notify('Client name is required', 'warning'); return; }
    try {
      await adminUpdateClient(instance, editClient.id, {
        clientname: editClient.clientname.trim(),
        logo: editClient.logo.trim() || null
      });
      notify('Client updated');
      setEditClientOpen(false);
      await refreshClients();
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to update client', 'error');
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Delete "${clientName}" and ALL its tm_ids, user access, and users? This cannot be undone.`)) return;
    try {
      await adminDeleteClient(instance, clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
      setEditClientOpen(false);
      setSelectedClient(null);
      notify(`Client "${clientName}" deleted`);
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to delete client', 'error');
    }
  };

  const handleAddTmId = async () => {
    if (!newTmId) { notify('Enter a tm_id', 'warning'); return; }
    try {
      await adminAddTmId(instance, selectedClient.id, parseInt(newTmId));
      notify('tm_id added');
      setNewTmId('');
      await refreshClients();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to add tm_id', 'error');
    }
  };

  const handleDeleteTmId = async (tmidRowId, tmIdVal) => {
    if (!window.confirm(`Remove tm_id ${tmIdVal}? This also removes all user access entries for it.`)) return;
    try {
      await adminDeleteTmId(instance, tmidRowId);
      notify('tm_id removed');
      await refreshClients();
      loadData();
    } catch (err) {
      notify(err.response?.data?.detail || 'Failed to remove tm_id', 'error');
    }
  };

  const getAvailableTmIds = () => {
    if (!selectedUser) return [];
    const client = clients.find(c => c.id === selectedUser.client_id);
    if (!client) return [];
    const assigned = new Set(accessEntries.map(e => e.tm_id));
    return (client.tm_ids || []).filter(t => !assigned.has(t.tm_id));
  };

  if (initialLoad) {
    return (
      <Box className="admin-portal" display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-portal">
      <div className="admin-tabs">
        <button type="button" className={`tab-button${activeTab === 0 ? ' active' : ''}`} onClick={() => setActiveTab(0)}>
          Users · {users.length}
        </button>
        <button type="button" className={`tab-button${activeTab === 1 ? ' active' : ''}`} onClick={() => setActiveTab(1)}>
          Clients · {clients.length}
        </button>
      </div>

      {activeTab === 0 && (
        <div className="admin-panel">
          <div className="admin-toolbar">
            <select
              className="admin-filter"
              aria-label="Filter by client"
              value={clientFilter || ''}
              onChange={(e) => setClientFilter(e.target.value || null)}
            >
              <option value="">All clients ({users.length})</option>
              {clients.map(c => {
                const count = users.filter(u => u.clientname === c.clientname).length;
                return <option key={c.id} value={c.clientname}>{c.clientname} ({count})</option>;
              })}
            </select>
            <button type="button" className="btn-primary" onClick={() => setAddUserOpen(true)}>Add user</button>
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">On</TableCell>
                  <TableCell align="center">Access</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.email}>
                    <TableCell className="admin-strong admin-card-title">{user.email}</TableCell>
                    <TableCell className="admin-card-meta">{user.clientname}</TableCell>
                    <TableCell className="admin-card-meta">{user.rolename}</TableCell>
                    <TableCell className="admin-card-foot" align="center">
                      <Switch checked={user.is_allowed} onChange={() => handleToggleUser(user.email)} size="small" />
                    </TableCell>
                    <TableCell className="admin-card-foot" align="center"><span className="admin-count">{user.access_count}</span></TableCell>
                    <TableCell className="admin-card-foot" align="right">
                      <button type="button" className="btn-secondary" onClick={() => openAccessDialog(user)}>Edit</button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredUsers.length === 0 && (
                  <TableRow className="admin-empty">
                    <TableCell colSpan={6} align="center" className="admin-muted">
                      {clientFilter ? `No users for ${clientFilter}` : 'No users found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      {activeTab === 1 && (
        <div className="admin-panel">
          <div className="admin-toolbar">
            <span />
            <button type="button" className="btn-primary" onClick={() => setAddClientOpen(true)}>Add client</button>
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>tm_ids</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="admin-card-title">
                      <div className="admin-client">
                        {client.logo ? (
                          <img className="admin-logo" src={client.logo} alt="" />
                        ) : (
                          <span className="admin-initial">{client.clientname?.charAt(0)?.toUpperCase()}</span>
                        )}
                        <span className="admin-strong">{client.clientname}</span>
                      </div>
                    </TableCell>
                    <TableCell className="admin-card-meta admin-card-ids">
                      {(client.tm_ids || []).length > 0 ? (
                        <span className="admin-ids">
                          {client.tm_ids.map(t => `${t.tm_id} — ${t.tm_name || '?'}`).join(', ')}
                        </span>
                      ) : (
                        <span className="admin-muted">None</span>
                      )}
                    </TableCell>
                    <TableCell className="admin-card-foot" align="right">
                      <button type="button" className="btn-secondary" onClick={() => openEditClient(client)}>Edit</button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      <Dialog open={editClientOpen} onClose={() => setEditClientOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="admin-strong">Edit — {selectedClient?.clientname}</DialogTitle>
        <DialogContent>
          <div className="admin-form">
            <TextField label="Client Name" value={editClient.clientname}
              onChange={(e) => setEditClient(prev => ({ ...prev, clientname: e.target.value }))}
              fullWidth size="small" required />
            <TextField label="Logo URL" value={editClient.logo}
              onChange={(e) => setEditClient(prev => ({ ...prev, logo: e.target.value }))}
              fullWidth size="small" placeholder="https://..." />
            {editClient.logo && (
              <div className="admin-preview">
                <img src={editClient.logo} alt="Preview"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="admin-muted">Logo preview</span>
              </div>
            )}
          </div>
          <TableContainer sx={{ mt: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>tm_id</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedClient?.tm_ids || []).map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="admin-strong">{t.tm_id}</TableCell>
                    <TableCell>{t.tm_name || '—'}</TableCell>
                    <TableCell align="right">
                      <button type="button" className="admin-icon" aria-label={`Remove tm_id ${t.tm_id}`}
                        onClick={() => handleDeleteTmId(t.id, t.tm_id)}>
                        <DeleteIcon fontSize="small" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!selectedClient?.tm_ids || selectedClient.tm_ids.length === 0) && (
                  <TableRow><TableCell colSpan={3} align="center" className="admin-muted">No tm_ids</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="admin-add-box">
            <p className="admin-strong">Add New tm_id</p>
            <div className="admin-add-row">
              <TextField label="tm_id (TMWIN CLIENT_ID)" value={newTmId} onChange={(e) => setNewTmId(e.target.value)}
                size="small" type="number" sx={{ minWidth: 220 }} />
              <button type="button" className="btn-primary" onClick={handleAddTmId}>Add</button>
            </div>
            <p className="admin-muted">Name looked up automatically from TMWIN.</p>
          </div>
        </DialogContent>
        <DialogActions>
          <button type="button" className="admin-danger"
            onClick={() => handleDeleteClient(editClient.id, editClient.clientname)}>Delete client</button>
          <span className="admin-add-row">
            <button type="button" className="btn-secondary" onClick={() => setEditClientOpen(false)}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleUpdateClient}>Save</button>
          </span>
        </DialogActions>
      </Dialog>

      <Dialog open={addClientOpen} onClose={() => setAddClientOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="admin-strong">Add New Client</DialogTitle>
        <DialogContent>
          <div className="admin-form">
            <TextField label="Client Name" value={newClient.clientname}
              onChange={(e) => setNewClient(prev => ({ ...prev, clientname: e.target.value }))}
              fullWidth size="small" required />
            <TextField label="Logo URL" value={newClient.logo}
              onChange={(e) => setNewClient(prev => ({ ...prev, logo: e.target.value }))}
              fullWidth size="small" placeholder="https://..." />
            {newClient.logo && (
              <div className="admin-preview">
                <img src={newClient.logo} alt="Preview"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="admin-muted">Logo preview</span>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <span />
          <span className="admin-add-row">
            <button type="button" className="btn-secondary"
              onClick={() => { setAddClientOpen(false); setNewClient({ clientname: '', logo: '' }); }}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleAddClient}>Create</button>
          </span>
        </DialogActions>
      </Dialog>

      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="admin-strong">Add New User</DialogTitle>
        <DialogContent>
          <div className="admin-form">
            <TextField label="Email" value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              fullWidth size="small" />
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select value={newUser.client_id} label="Client"
                onChange={(e) => setNewUser(prev => ({ ...prev, client_id: e.target.value }))}>
                {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.clientname}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={newUser.roleid} label="Role"
                onChange={(e) => setNewUser(prev => ({ ...prev, roleid: e.target.value }))}>
                {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.rolename}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <span />
          <span className="admin-add-row">
            <button type="button" className="btn-secondary" onClick={() => setAddUserOpen(false)}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleAddUser}>Add User</button>
          </span>
        </DialogActions>
      </Dialog>

      <Dialog open={accessDialogOpen} onClose={closeAccessDialog} maxWidth="md" fullWidth>
        <DialogTitle className="admin-strong">
          Edit — {selectedUser?.email}
          <Typography variant="body2" className="admin-muted">Client: {selectedUser?.clientname}</Typography>
        </DialogTitle>
        <DialogContent>
          <div className="admin-form">
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select value={selectedUser?.client_id ?? ''} label="Client"
                onChange={(e) => handleClientChange(selectedUser.email, e.target.value)}>
                {clients.map(c => <MenuItem key={c.id} value={c.id}>{c.clientname}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select value={selectedUser?.roleid ?? ''} label="Role"
                onChange={(e) => handleRoleChange(selectedUser.email, e.target.value)}>
                {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.rolename}</MenuItem>)}
              </Select>
            </FormControl>
          </div>
          {accessLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <>
              <TableContainer sx={{ mt: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>tm_id</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Access Type</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accessEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="admin-strong">{entry.tm_id}</TableCell>
                        <TableCell>{entry.tm_name || '—'}</TableCell>
                        <TableCell>
                          <Select value={entry.access_type}
                            onChange={(e) => handleUpdateAccess(entry.id, e.target.value)}
                            size="small" variant="standard" sx={{ minWidth: 100 }}>
                            <MenuItem value="both">Both</MenuItem>
                            <MenuItem value="pickup">Pickup</MenuItem>
                            <MenuItem value="dropoff">Dropoff</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell align="right">
                          <button type="button" className="admin-icon" aria-label={`Remove access ${entry.tm_id}`}
                            onClick={() => handleDeleteAccess(entry.id)}>
                            <DeleteIcon fontSize="small" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {accessEntries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" className="admin-muted">
                          No access entries. Add tm_ids below.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <div className="admin-add-box">
                <p className="admin-strong">Add tm_id Access</p>
                <div className="admin-add-row">
                  <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>tm_id</InputLabel>
                    <Select value={newAccess.tm_id} label="tm_id"
                      onChange={(e) => setNewAccess(prev => ({ ...prev, tm_id: e.target.value }))}>
                      {getAvailableTmIds().map(t => (
                        <MenuItem key={t.tm_id} value={t.tm_id}>{t.tm_id} — {t.tm_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Access Type</InputLabel>
                    <Select value={newAccess.access_type} label="Access Type"
                      onChange={(e) => setNewAccess(prev => ({ ...prev, access_type: e.target.value }))}>
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="pickup">Pickup</MenuItem>
                      <MenuItem value="dropoff">Dropoff</MenuItem>
                    </Select>
                  </FormControl>
                  <button type="button" className="btn-primary" onClick={handleAddAccess}>Add</button>
                </div>
                {getAvailableTmIds().length === 0 && accessEntries.length > 0 && (
                  <p className="admin-muted">All tm_ids for this client have been assigned.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <button type="button" className="admin-danger" onClick={() => handleDeleteUser(selectedUser.email)}>Delete user</button>
          <button type="button" className="btn-secondary" onClick={closeAccessDialog}>Close</button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPortal;
